import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  PlusIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { fetchHseEvents } from "../../../../api/hse/events";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { HseEventStatistics } from "../../../../components/dashboard/hse/hseEventStatistics";
import { HseEventListRow } from "../../../../components/dashboard/hse/HseEventListRow";
import { PMDialog } from "../../../../components/utils/PMDialog";
import { EventSubnav } from "../../../../components/dashboard/hse/hseEventTabs";
import { HseListCard, HsePageBody } from "../../../../components/dashboard/hse/HsePageBody";

/**
 * HSE/KS Events List - Overview of all events (ISO 9001:2015 compliant)
 * Shows all event types with filtering options
 */
export const HseKsEventsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    event_type: "",
    event_category: "",
    status: "",
    start_date: "",
    end_date: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  });
  const [showStatistics, setShowStatistics] = useState(false);

  const [activeTab, setActiveTab] = useState(() => {
    const path = location.pathname;
    if (path.includes("/deviations")) return "deviations";
    if (path.includes("/near-misses")) return "near-misses";
    if (path.includes("/accidents")) return "accidents";
    if (path.includes("/complaints")) return "complaints";
    if (path.includes("/observations")) return "observations";
    return "all";
  });

  // Map ISO event types to backend filters
  const getEventFilters = () => {
    const baseFilters = { ...filters };
    
    switch (activeTab) {
      case "deviations":
        // Avvik: law_violation, regulation_violation, procedure_violation
        baseFilters.event_category = "law_violation,regulation_violation,procedure_violation";
        break;
      case "near-misses":
        // Nestenulykker: near_miss event type
        baseFilters.event_type = "near_miss";
        break;
      case "accidents":
        // Ulykker: injury event type
        baseFilters.event_type = "injury";
        break;
      case "complaints":
        // Reklamasjoner: can use event_category "other" or a specific category
        baseFilters.event_category = "other";
        break;
      case "observations":
        // Observasjoner: information category
        baseFilters.event_category = "information";
        break;
      default:
        // All: no additional filters
        break;
    }
    
    return baseFilters;
  };

  const fetchEvents = async (page = 1) => {
    try {
      setLoading(true);
      const eventFilters = getEventFilters();
      const params = {
        ...eventFilters,
        page,
        per_page: pagination.per_page,
      };
      const response = await fetchHseEvents(params);
      setEvents(response.data || []);
      setPagination({
        ...pagination,
        page,
        total: response.meta?.total || 0,
        total_pages: response.meta?.total_pages || 0,
      });
    } catch (err) {
      console.error("Error fetching HSE events:", err);
      setError(true);
      toast.error(t("Kunne ikke laste hendelser"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [filters, activeTab]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page) => {
    fetchEvents(page);
  };

  const headerActions = [
    {
      component: null,
      label: t("Statistikk"),
      action: () => setShowStatistics(true),
      icon: CheckCircleIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
  ];

  return (
    <>
      <HsePageChrome
        variant="list"
        title={t("hse.events.title")}
        subtitle={t("hse.events.subtitle")}
        listCount={loading ? null : pagination.total}
        actions={headerActions}
        mainButton={{
          label: t("hse.events.new"),
          action: () => navigate("/dashboard/hse-ks/events/new"),
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <HsePageBody>
            <EventSubnav active={activeTab} />

            <ModuleFilter
              filterCard
              hideStatusFilter
              searchPlaceholder={t("hse.events.search")}
              initialFilters={{
                query: filters.search,
                event_statuses: filters.status ? [filters.status] : [],
                event_types: filters.event_type ? [filters.event_type] : [],
              }}
              onFiltersChange={(next) =>
                handleFilterChange({
                  ...filters,
                  search: next.query || "",
                  status: next.event_statuses?.[0] || "",
                  event_type: next.event_types?.[0] || "",
                })
              }
              customDropdownFilters={[
                {
                  key: "event_statuses",
                  label: t("Status"),
                  options: [
                    { value: "draft", label: t("Kladd"), class: "bg-slate-100 text-slate-800" },
                    { value: "submitted", label: t("Innsendt"), class: "bg-slate-100 text-slate-800" },
                    { value: "under_review", label: t("Under vurdering"), class: "bg-slate-100 text-slate-800" },
                    { value: "closed", label: t("Lukket"), class: "bg-slate-100 text-slate-800" },
                  ],
                },
                {
                  key: "event_types",
                  label: t("Hendelsestype"),
                  options: [
                    { value: "injury", label: t("hse.events.type.injury"), class: "bg-slate-100 text-slate-800" },
                    { value: "near_miss", label: t("hse.events.type.near_miss"), class: "bg-slate-100 text-slate-800" },
                    { value: "property_damage", label: t("hse.events.type.property_damage"), class: "bg-slate-100 text-slate-800" },
                    { value: "environmental", label: t("hse.events.type.environmental"), class: "bg-slate-100 text-slate-800" },
                    { value: "other", label: t("hse.events.type.other"), class: "bg-slate-100 text-slate-800" },
                  ],
                },
              ]}
            />

            <HseListCard>
              {loading && events.length === 0 ? (
                <div className="p-8">
                  <ResourceLoading size="base" />
                </div>
              ) : error ? (
                <ResourceError errors={[t("Kunne ikke laste hendelser")]} onRetry={() => fetchEvents()} />
              ) : !events || events.length === 0 ? (
                <div className="px-6 py-16">
                  <ResourceEmpty message={t("hse.events.empty")} />
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {events.map((event) => (
                    <HseEventListRow
                      key={event.id}
                      event={event}
                      onChanged={() => fetchEvents(pagination.page)}
                    />
                  ))}
                </ul>
              )}
            </HseListCard>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    {t("Viser")} {(pagination.page - 1) * pagination.per_page + 1} -{" "}
                    {Math.min(
                      pagination.page * pagination.per_page,
                      pagination.total
                    )}{" "}
                    {t("av")} {pagination.total} {t("resultater")}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("Forrige")}
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.total_pages}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("Neste")}
                    </button>
                  </div>
                </div>
              </div>
            )}
        </HsePageBody>
      </HsePageChrome>

      {/* Statistics Dialog */}
      <PMDialog
        open={showStatistics}
        onClose={() => setShowStatistics(false)}
        title={t("Hendelsesstatistikk")}
        size="4xl"
      >
        <HseEventStatistics onClose={() => setShowStatistics(false)} />
      </PMDialog>
    </>
  );
};

